/* eslint-disable @typescript-eslint/naming-convention */
import { isBoolean, isNumber } from 'lodash';
import { loadImageAsync } from '@/utils/file-kit';
import { rotatePoint } from '@/utils/math-kit';
import type { ILayerProps } from '@/version-types';
import type { IService } from '../../shared';
import type { EdgelessObject } from '../canvas/edgeless-object';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';

/** 水印数据，原 SvipWatermarkPlugin 形态 */
export interface IWatermarkStageInfo {
    watermarkLayers: ILayerProps[];
    isGlobal: boolean;
}

export type TWatermarkData = Map<number, IWatermarkStageInfo>;

/** SVIP 水印统一透明度 */
const WATERMARK_ALPHA = 0.6;

/**
 * SVIP 水印服务
 * 替代原来的 svip-watermark plugin
 * 1. 图层水印：通过 EdgelessObject 在画布上叠加绘制
 * 2. 全局水印：通过 EdgelessFrame 在 frame 渲染时叠加绘制
 */
export class SvipWatermarkService implements IService {
    private __type = 'svip-watermark';

    /** 当前水印数据，按 stageIndex 维度组织 */
    private __watermarkData: TWatermarkData = new Map();
    /** 当前激活图层是否需要水印 */
    private __activeLayerWatermark: boolean = false;
    /** 水印图片 URL（base64 or 普通 URL） */
    private __watermarkImageUrl: string = '';
    /** 已加载的水印图片元素 */
    private __watermarkImage: HTMLImageElement | null = null;
    /** 加载中的 promise，避免并发加载 */
    private __watermarkLoadingPromise: Promise<HTMLImageElement | null> | null = null;

    /** 上一次启用图层水印的 layerId 集合，用于计算 diff */
    private __prevLayerIds: Set<string> = new Set();
    /** 上一次启用全局水印的 stageIndex 集合 */
    private __prevGlobalStageIndex: Set<number> = new Set();
    /** 上一次激活图层 id（用于撤销 activeLayerWatermark） */
    private __prevActiveLayerId: string | null = null;

    constructor(readonly service: ServiceManager) {
        new EventGenerator('svip-watermark', this, service);
    }

    onInit() {
        this.__watermarkData = new Map();
        this.__activeLayerWatermark = false;
        this.__prevLayerIds.clear();
        this.__prevGlobalStageIndex.clear();
        this.__prevActiveLayerId = null;
    }

    /** 设置水印图片 */
    setWatermarkImage(image: string) {
        if (this.__watermarkImageUrl === image) return;
        this.__watermarkImageUrl = image;
        this.__watermarkImage = null;
        this.__watermarkLoadingPromise = null;
        // 触发预加载
        void this.loadWatermarkImage().then(() => {
            this.__refreshLayerWatermarks();
            this.__refreshGlobalWatermarks();
        });
    }

    /** 获取水印图片 URL */
    getWatermarkImage() {
        return this.__watermarkImageUrl;
    }

    /** 获取已加载完成的水印图片元素（同步） */
    getWatermarkImageEl() {
        return this.__watermarkImage;
    }

    /** 异步加载水印图片 */
    loadWatermarkImage() {
        if (this.__watermarkImage) return Promise.resolve(this.__watermarkImage);
        if (!this.__watermarkImageUrl) return Promise.resolve(null);
        if (this.__watermarkLoadingPromise) return this.__watermarkLoadingPromise;
        this.__watermarkLoadingPromise = loadImageAsync(this.__watermarkImageUrl, false)
            .then((image: HTMLImageElement) => {
                this.__watermarkImage = image;
                return image;
            })
            .catch(() => null);
        return this.__watermarkLoadingPromise;
    }

    /** 获取水印数据 */
    getWatermarkData() {
        return this.__watermarkData;
    }

    /** 当前激活图层是否启用水印 */
    isActiveLayerWatermark() {
        return this.__activeLayerWatermark;
    }

    /** 判断指定 layerId 是否需要绘制图层水印 */
    hasLayerWatermark(layerId: string) {
        return this.__prevLayerIds.has(layerId);
    }

    /** 判断指定 layerId 当前是否需要在 render 阶段叠加 SVIP 水印 */
    shouldRenderLayerWatermark(layerId: string) {
        if (this.__isLayerInGlobalWatermarkFrame(layerId)) return false;
        return this.__prevLayerIds.has(layerId) || this.__prevActiveLayerId === layerId;
    }

    /** 判断指定 frameId 是否需要绘制全局水印 */
    hasGlobalWatermark(frameId: number) {
        const stageIndex = this.service.frame.frameIdToIndex(frameId);
        return this.__prevGlobalStageIndex.has(stageIndex);
    }

    /** 在 frame render 阶段绘制全局 SVIP 水印 */
    drawFrameWatermark(
        frameId: number,
        ctx: CanvasRenderingContext2D | null,
        x: number,
        y: number,
        width: number,
        height: number,
    ) {
        if (!this.hasGlobalWatermark(frameId)) return;
        const image = this.getWatermarkImageEl();
        if (!ctx || !image) return;
        this.__paintFrameWatermarkPattern(ctx, image, x, y, width, height);
    }

    /**
     * 在图层 render 阶段按配方边界绘制 SVIP 水印
     *
     * drawable 的边界可能因阴影、描边等效果偏离配方边界，水印尺寸与位置不能依赖 drawable。
     *
     * @param object - 当前图层的 drawable
     * @param ctx - drawable 的局部画布上下文
     */
    drawLayerWatermark(object: EdgelessObject, ctx: CanvasRenderingContext2D | null) {
        if (!this.shouldRenderLayerWatermark(object.layerId)) return;
        const image = this.getWatermarkImageEl();
        if (!ctx || !image) return;
        const layer = this.service.flatLayer.getLayer(object.layerId);
        const { width, height } = layer;
        const tileSize = Math.min(width, height);
        if (tileSize <= 0) return;

        const recipeTopLeft = object.calcCenterIsLeftTopPosition({
            left: layer.left,
            top: layer.top,
            width,
            height,
            rotate: layer.rotate,
        });
        const offset = rotatePoint(
            {
                x: recipeTopLeft.left - object.left,
                y: recipeTopLeft.top - object.top,
            },
            -object.angle,
        );
        const pattern = ctx.createPattern(image, 'repeat');
        if (!pattern) return;
        const ratio = tileSize / image.width;
        if (typeof DOMMatrix !== 'undefined') {
            const matrix = new DOMMatrix();
            matrix.scaleSelf(ratio, ratio);
            pattern.setTransform(matrix);
        }
        ctx.save();
        ctx.translate(offset.x, offset.y);
        ctx.globalAlpha *= WATERMARK_ALPHA;
        ctx.beginPath();
        ctx.rect(0, 0, width, height);
        ctx.clip();
        ctx.fillStyle = pattern;
        ctx.fill();
        ctx.restore();
    }

    /** 绘制 frame 全局水印的 pattern 平铺 */
    private __paintFrameWatermarkPattern(
        ctx: CanvasRenderingContext2D,
        image: HTMLImageElement,
        x: number,
        y: number,
        width: number,
        height: number,
    ) {
        const tileSize = Math.round(Math.min(width, height) / 3);
        if (tileSize <= 0) return;
        const pattern = ctx.createPattern(image, 'repeat');
        if (!pattern) return;
        const ratio = tileSize / image.width;
        if (typeof DOMMatrix !== 'undefined') {
            const matrix = new DOMMatrix();
            matrix.translateSelf(x, y);
            matrix.scaleSelf(ratio, ratio);
            pattern.setTransform(matrix);
        }
        ctx.save();
        ctx.globalAlpha *= WATERMARK_ALPHA;
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.clip();
        ctx.fillStyle = pattern;
        ctx.fill();
        ctx.restore();
    }

    /** 设置激活图层水印，true 时给当前 activePickLayer 加水印 */
    setActiveLayerWatermark(needRender: boolean) {
        const activeLayerId = needRender ? (this.service.frame.getActivePickLayer()?.id ?? null) : null;
        if (this.__activeLayerWatermark === needRender && (!needRender || this.__prevActiveLayerId === activeLayerId)) {
            return;
        }
        this.__activeLayerWatermark = needRender;

        // 先关闭前一个 active 图层的水印
        if (this.__prevActiveLayerId) {
            const prevLayerId = this.__prevActiveLayerId;
            this.__prevActiveLayerId = null;
            this.__applyLayerWatermark(prevLayerId);
        }

        if (activeLayerId) {
            this.__prevActiveLayerId = activeLayerId;
            this.__applyLayerWatermark(activeLayerId);
        }

        this.service.events.emit(this.__type, this.__watermarkData, needRender);
    }

    /**
     * 渲染水印
     * @param data - 水印数据，为空时清空
     */
    renderWatermark(data?: TWatermarkData) {
        console.log('[SvipWatermarkService] renderWatermark', data);

        const newData = data ?? new Map();
        this.__watermarkData = newData;

        // 1) 计算图层水印的 diff
        const nextLayerIds = new Set<string>();
        const nextGlobalStageIndex = new Set<number>();
        newData.forEach((info, stageIndex) => {
            if (info.isGlobal) {
                nextGlobalStageIndex.add(stageIndex);
            } else {
                info.watermarkLayers.forEach((layer) => {
                    if (layer.id) nextLayerIds.add(layer.id);
                });
            }
        });

        const dirtyLayerIds = new Set<string>();
        // 2) 记录不再需要的图层
        this.__prevLayerIds.forEach((layerId) => {
            if (!nextLayerIds.has(layerId)) {
                dirtyLayerIds.add(layerId);
            }
        });
        // 3) 记录新需要的图层
        nextLayerIds.forEach((layerId) => {
            if (!this.__prevLayerIds.has(layerId)) {
                dirtyLayerIds.add(layerId);
            }
        });

        // 4) 全局水印 diff，标记 frame 为脏
        const dirtyStageIndexes = new Set<number>();
        this.__prevGlobalStageIndex.forEach((idx) => {
            if (!nextGlobalStageIndex.has(idx)) dirtyStageIndexes.add(idx);
        });
        nextGlobalStageIndex.forEach((idx) => {
            if (!this.__prevGlobalStageIndex.has(idx)) dirtyStageIndexes.add(idx);
        });
        this.__prevLayerIds = nextLayerIds;
        this.__prevGlobalStageIndex = nextGlobalStageIndex;

        dirtyLayerIds.forEach((layerId) => {
            this.__applyLayerWatermark(layerId);
        });

        dirtyStageIndexes.forEach((stageIndex) => {
            const frameId = this.service.frame.indexToFrameId(stageIndex);
            if (isNumber(frameId)) {
                this.service.edgelessCanvas.addDirtyFrameId(frameId);
                this.__applyFrameLayerWatermarks(frameId);
            }
        });
        if (dirtyStageIndexes.size) {
            // 保证图片加载完成后再渲染一次
            void this.loadWatermarkImage().then(() => {
                this.service.edgelessCanvas.requestRenderAll();
            });
            this.service.edgelessCanvas.requestRenderAll();
        }

        this.service.events.emit(this.__type, this.__watermarkData);
    }

    /**
     * 监听水印数据变化（兼容旧 onWatermark API）
     */
    onWatermark(fn: (data: TWatermarkData, activeLayerWatermark?: boolean) => void) {
        return this.service.events.on(this.__type, (data: TWatermarkData, activeLayerWatermark?: boolean) => {
            fn(data, isBoolean(activeLayerWatermark) ? activeLayerWatermark : undefined);
        });
    }

    private __applyLayerWatermark(layerId: string) {
        const object = this.service.edgelessCanvas.getByLayerId(layerId);
        if (!object) return;
        const apply = () => {
            this.service.edgelessCanvas.setDirty({ object });
            // 组合的 setDirty 只标记 frame，水印变化时需要额外请求重绘。
            if (object.noRender) {
                this.service.edgelessCanvas.requestRenderAll();
            }
        };
        const image = this.getWatermarkImageEl();
        if (image || !this.shouldRenderLayerWatermark(layerId)) {
            apply();
        } else {
            void this.loadWatermarkImage().then(apply);
        }
    }

    private __applyFrameLayerWatermarks(frameId: number) {
        this.service.flatLayer.traverseIds(true, this.service.frame.getLayerIds(frameId), (layerId: string) => {
            this.__applyLayerWatermark(layerId);
        });
    }

    private __refreshLayerWatermarks() {
        const layerIds = new Set(this.__prevLayerIds);
        if (this.__prevActiveLayerId) {
            layerIds.add(this.__prevActiveLayerId);
        }
        layerIds.forEach((layerId) => {
            this.__applyLayerWatermark(layerId);
        });
    }

    private __refreshGlobalWatermarks() {
        this.__prevGlobalStageIndex.forEach((stageIndex) => {
            const frameId = this.service.frame.indexToFrameId(stageIndex);
            if (isNumber(frameId)) {
                this.service.edgelessCanvas.addDirtyFrameId(frameId);
            }
        });
        if (this.__prevGlobalStageIndex.size) {
            this.service.edgelessCanvas.requestRenderAll();
        }
    }

    private __isLayerInGlobalWatermarkFrame(layerId: string) {
        const frameId = this.service.frame.getLayerFrameId(layerId);
        if (!isNumber(frameId)) return false;
        const stageIndex = this.service.frame.frameIdToIndex(frameId);
        return this.__prevGlobalStageIndex.has(stageIndex);
    }
}
